import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { UserRole, ROLE_LABELS } from '@/types/user';
import { projectAPI } from '@/utils/supabase/client';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function InviteUserDialog({ open, onOpenChange, onSuccess }: InviteUserDialogProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('team_member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const result = await projectAPI.inviteUser({
        email,
        displayName: displayName || undefined,
        role,
      });

      // Check if email was sent successfully
      if (!result.emailSent) {
        setWarning(`User profile created but invite email failed: ${result.emailError || 'Unknown error'}. You can try "Resend Invite" from the user list.`);
        // Still call onSuccess to refresh the list and show the user
        onSuccess();
        return;
      }

      // Reset form
      setEmail('');
      setDisplayName('');
      setRole('team_member');
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setDisplayName('');
      setRole('team_member');
      setError(null);
      setWarning(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif">Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new team member.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Display Name (optional) */}
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="John Smith"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Optional. The user can set this themselves later.
              </p>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select value={role} onValueChange={(value: UserRole) => setRole(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">
                    {ROLE_LABELS.owner}
                    <span className="text-muted-foreground ml-2">- View all, edit projects</span>
                  </SelectItem>
                  <SelectItem value="pipeline_manager">
                    {ROLE_LABELS.pipeline_manager}
                    <span className="text-muted-foreground ml-2">- Prospect pipelines only</span>
                  </SelectItem>
                  <SelectItem value="team_lead">
                    {ROLE_LABELS.team_lead}
                    <span className="text-muted-foreground ml-2">- Assigned projects + pipelines</span>
                  </SelectItem>
                  <SelectItem value="team_member">
                    {ROLE_LABELS.team_member}
                    <span className="text-muted-foreground ml-2">- Assigned projects only</span>
                  </SelectItem>
                  <SelectItem value="external_partner">
                    {ROLE_LABELS.external_partner}
                    <span className="text-muted-foreground ml-2">- Granted projects only</span>
                  </SelectItem>
                  <SelectItem value="intern">
                    {ROLE_LABELS.intern}
                    <span className="text-muted-foreground ml-2">- Intern OS section only</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The role determines what the user can access.
              </p>
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            {/* Warning message (email failed but profile created) */}
            {warning && (
              <div className="p-3 bg-warning/15 border border-warning/20 rounded-md">
                <p className="text-sm text-warning">{warning}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !email}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Invitation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
